import { colorSet } from '@components/Provider';
import { makeStyles, Theme } from '@material-ui/core';
import {
  CSSProperties,
  CreateCSSProperties
} from '@material-ui/core/styles/withStyles';

import ChatHeaderStyleSheet from './ChatHeader/styleSheet';
import ChatMessageStyleSheet from './ChatMessagesView/styleSheet';
import ChatMessageInputStyleSheet from './ChatMessagesView/ChatMessageInput/styleSheet';

import {
  CHAT_DRAWER_TITLE_BAR_HEIGHT,
  CHAT_MENU_WIDTH,
  CHAT_MOBILE_HEADER_HEIGHT,
  CHAT_MOBILE_MENU_HEIGHT,
  GNB_HEIGHT
} from './constants';

type StyleSheetType = CSSProperties | CreateCSSProperties<{}>;

const useStyles = makeStyles((theme) => {
  const isMobile = theme.breakpoints.down('md');

  return {
    'messageRef': {
      marginBottom: isMobile ? '18px' : '20px'
    },
    'Chat-Dropzone-Provider': {
      height: `calc(100% - ${CHAT_MOBILE_HEADER_HEIGHT}px)`,
      [theme.breakpoints.down('md')]: {
        height: '100%'
      }
    },
    'Chat-Empty-root': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100%',
      width: '100%',
      height: '100%',
      zIndex: 1100,
      backgroundColor: `${colorSet.gray100}`,
      '& .MuiTypography-h4': {
        color: `${colorSet.gray500}`
      }
    },
    'Chat-Container-root': {
      display: 'flex',
      width: '100%',
      height: '100%',
      [isMobile]: {
        flexDirection: 'column'
      }
    },
    'Chat-View-root': {
      width: '100%',
      height: '100%',
      backgroundColor: colorSet.gray200,
      [isMobile]: {
        height: `calc(100% - ${
          CHAT_MOBILE_MENU_HEIGHT + CHAT_MOBILE_HEADER_HEIGHT + 1
        }px)`
      }
    },
    'Chat-Messages-root': {
      ...ChatMessageStyleSheet(isMobile, theme)
    },
    'Chat-Menu-root': {
      ...ChatSideMenuStyleSheet(isMobile, theme)
    },
    'Chat-Drawer-root': {
      ...ChatDrawerStyleSheet(isMobile, theme)
    },
    'Chat-Popover-root': {
      boxShadow:
        '0px 3px 14px 2px #0000000f, 0px 8px 10px 1px #00000012, 0px 5px 5px -3px #0000001a',
      '& .MuiPaper-root': {
        borderRadius: 8,
        width: 300,
        minHeight: 64
      },
      '& .consulting-close-btn': {
        backgroundColor: colorSet.gray000,
        height: 64,
        '& > span': {
          fontSize: 16,
          color: colorSet.errorMain
        },
        '&.closed': {
          opacity: 0.4
        }
      }
    },
    'Chat-Header-root': {
      ...ChatHeaderStyleSheet(isMobile, theme)
    },
    'Chat-Message-Input-root': {
      ...ChatMessageInputStyleSheet(isMobile, theme)
    },
    'Chat-Menu-Fixed-Bottom-CTA': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: 89,
      position: 'fixed',
      bottom: 0,
      width: `calc(100% - ${CHAT_MENU_WIDTH}px)`,
      backgroundColor: colorSet.gray000,
      border: `1px solid ${colorSet.gray400}`,
      padding: '0px 24px',
      [theme.breakpoints.down('md')]: {
        width: 'calc(100%)',
        height: 81
      },
      '&.empty': {
        position: 'relative',
        minHeight: 89,
        zIndex: -1,
        [theme.breakpoints.down('md')]: {
          minHeight: 81
        }
      },
      '& .cta': {
        maxWidth: 168,
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        },
        '&.edit': {
          marginRight: 12
        }
      }
    },
    'Chat-Input-Bypass-Warning-Bar-root': {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '16px',
      gap: '8px',
      backgroundColor: colorSet.errorLight,
      '& svg': {
        minWidth: '20px'
      },
      '& .warning-text': {
        color: colorSet.errorMain
      }
    }
  };
});

const ChatDrawerStyleSheet = (
  isMobile: string,
  theme: Theme
): StyleSheetType => {
  return {
    position: 'relative',
    '& .consulting-close-btn': {
      backgroundColor: colorSet.gray000,
      height: 61,
      padding: '6px 8px',
      '& > span': {
        fontSize: 16,
        color: colorSet.errorMain
      },
      '&.closed': {
        opacity: 0.4
      }
    },
    '& .MuiBackdrop-root': {
      zIndex: theme.zIndex.modal + 1
    },
    '& .drawer-paper': {
      zIndex: theme.zIndex.modal + 2,
      width: `calc(100% - ${CHAT_MENU_WIDTH}px)`,
      height: `calc(100vh - ${GNB_HEIGHT}px)`,
      backgroundColor: colorSet.gray000,
      top: 61,
      left: 0,
      [isMobile]: {
        width: '100%',
        height: '100%',
        top: 0,
        paddingRight: 0
      },
      '&.kebab': {
        backgroundColor: 'transparent',
        top: 'unset',
        left: 'inherit',
        height: 61,
        width: '100%',
        bottom: 0,
        borderRadius: '16px 16px 0px 0px'
      }
    },

    '& .drawer-header': {
      position: 'fixed',
      width: `calc(100% - ${CHAT_MENU_WIDTH}px)`,
      height: `${CHAT_DRAWER_TITLE_BAR_HEIGHT}px`,
      borderBottom: `1px solid ${colorSet.gray400}`,
      borderRight: `1px solid ${colorSet.gray400}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px 16px',
      zIndex: theme.zIndex.drawer + 2,
      backgroundColor: colorSet.gray000,
      [isMobile]: {
        width: 'calc(100%)'
      },
      '& .drawer-close': {
        position: 'absolute',
        display: 'flex',
        top: 12,
        left: 16,
        '& .close-button': {
          position: 'unset',
          padding: 0,
          width: 24,
          height: 24
        }
      },
      '&.empty': {
        minHeight: 49,
        position: 'relative',
        zIndex: -1,
        visibility: 'hidden'
      }
    },
    '& .drawer-content': {
      width: '100%',
      '&.request': {
        marginTop: 16,
        marginBottom: 16,
        [theme.breakpoints.down('md')]: {
          margin: 0
        }
      },
      '&.dm': {
        margin: '48px 0px',
        [theme.breakpoints.down('md')]: {
          margin: 0
        }
      },
      '&.viewer': {
        margin: 0,
        height: '100%'
      },
      '&.kebab-menu': {
        height: 61,
        backgroundColor: colorSet.gray000,
        padding: 0
      },
      '&.purchaseorder': {
        display: 'flex',
        flexDirection: 'column'
      },
      '&.quotation': {
        height: '100%'
      }
    }
  };
};

const ChatSideMenuStyleSheet = (
  isMobile: string,
  theme: Omit<Theme, 'breakpoints'>
): StyleSheetType => {
  return {
    backgroundColor: colorSet.gray000,
    zIndex: theme.zIndex?.drawer + 1,
    maxWidth: 70,
    width: '100%',
    height: '100%',
    borderLeft: `1px solid ${colorSet.gray400}`,
    display: 'flex',
    flexDirection: 'column',
    [isMobile]: {
      maxWidth: '100%',
      height: `${CHAT_MOBILE_HEADER_HEIGHT + CHAT_MOBILE_MENU_HEIGHT + 1}px`,
      borderLeft: 'none',
      borderBottom: `1px solid ${colorSet.gray400}`
    },
    '& .selected': {
      backgroundColor: colorSet.gray200
    },
    '& .menu-list-root': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      [isMobile]: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    '& .menu-item-root': {
      maxWidth: 69,
      maxHeight: 69,
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      '&.isHelper': {
        marginTop: 'auto'
      },
      '& .menu-item-icon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '100%',
        marginBottom: 2,
        height: 26,
        '& svg': {
          width: 26,
          height: 26,
          color: colorSet.gray900
        }
      },
      '& .menu-item-text': {
        marginTop: 0,
        marginBottom: 0,
        '& > span': {
          fontSize: 10,
          fontWeight: 400,
          lineHeight: '18px',
          color: colorSet.gray900,
          whiteSpace: 'nowrap'
        }
      }
    }
  };
};

export default useStyles;
